import { FC, useEffect, useState } from 'react';
import { VisitorPass, VisitorPasses } from '../../models/IVisitorPass';
import { getPassesByPageAndFilter } from '../../services/pass-service';
import { useUserContext } from '../../context/user';
import { useI18nContext } from '../../context/language/use-i18n-context';
import ts from './i18n';
import { Icon, IconButton, Table, TableCell, TableHeading, Skeleton } from '@nike/eds';
import { formatDate } from '../../utils/date-helper';
import dayjs from 'dayjs';
import { AppConfig } from '../../utils/constants';
import styles from './visitor-pass.module.css';
import { PASSSTATUSES, PASSTYPES } from './options';
import { VisitorPassHeader } from './visitorpass-header/visitorpass-header';
import {
  EditPassProps,
  FilterStatusAndPage,
  Option,
  Props,
  VisitorPassCriteria,
  VistorPassProps,
} from './type';
import { CancelPassModal } from './cancel-pass/cancel-pass';
import { ResendEmailModal } from './resend-email/resend-email';
import Pass from '../../components/passes/pass_container/pass';
import LegalTextBanner from '../../components/shared/legaltext-banner/legaltext-banner';
type TsKeys = keyof typeof ts;
export const VisitorPassGrid: FC<Props> = ({ passPolicies, isModelOpen, setSnackBar }) => {
  const { i18nString } = useI18nContext();
  const [visitorPasses, setVisitorPasses] = useState<VisitorPasses>();
  const [sorting, setSorting] = useState({ key: 'name', asc: true });
  const [visitorPassFilter, setVisitorPassFilter] = useState<VisitorPassCriteria>({
    passType: 0,
    passStatus: 0,
    pageSize: 10,
    currentPage: 1,
  });
  const [passTypes, setPassTypes] = useState<Option[]>();
  const [loading, setLoading] = useState<boolean>(false);
  const userInfo = useUserContext();
  const [passProps, setPassProps] = useState<VistorPassProps>({
    pass: {} as VisitorPass,
    onDismiss: () => setPassProps({ ...passProps, isVisible: false, isResendVisible: false }),
    isVisible: false,
    isResendVisible: false,
  });

  const columns = [
    { headerName: ts.GRID_HEADER_NAME.stringKey, field: 'name' },
    {
      headerName: ts.GRID_HEADER_COMPANY.stringKey,
      field: 'companyName',
    },
    {
      headerName: ts.GRID_HEADER_TYPE.stringKey,
      field: 'passTypeToken',
    },
    {
      headerName: ts.GRID_HEADER_PASS_TYPE.stringKey,
      field: 'passPolicy',
    },
    { headerName: ts.GRID_HEADER_DATE.stringKey, field: 'date' },
    {
      headerName: ts.GRID_HEADER_STATUS.stringKey,
      field: 'statusToken',
    },
  ];

  useEffect(() => {
    const psTypes: Option[] = [PASSTYPES[0]];
    const availablePasstypeIds = new Set(passPolicies?.map(pol => pol.passTypeId));

    PASSTYPES.forEach(passType => {
      if (availablePasstypeIds.has(passType.value)) {
        psTypes.push(passType);
      }
    });
    setPassTypes(psTypes);
  }, [passPolicies]);

  const filterAndPageChange = (value: FilterStatusAndPage) => {
    setVisitorPassFilter({
      passType: value.passType?.value || 0,
      passStatus: value.passStatus?.value || 0,
      pageSize: 10,
      currentPage: value.page,
    });
  };
  const [editPassProps, setEditPassProps] = useState<EditPassProps>({
    modalOpen: false,
    onDismiss: () => setEditPassProps({ ...editPassProps, modalOpen: false }),
    passToEdit: null,
  });
  useEffect(() => {
    const sponsorId = userInfo?.passplayUser?.sponsorId;
    if (
      sponsorId &&
      isModelOpen === false &&
      passProps.isVisible === false &&
      editPassProps.modalOpen === false
    ) {
      setLoading(true);
      const visitStatus = visitorPassFilter.passStatus === 0 ? '0,1,4,8' : '';
      const visitType = visitorPassFilter.passType === 0 ? null : visitorPassFilter.passType;
      getPassesByPageAndFilter(
        sponsorId,
        visitorPassFilter.currentPage,
        visitorPassFilter.pageSize,
        visitType,
        visitStatus,
      )
        .then(passes => {
          passes.data.passes.forEach(pass => (pass.date = formatDate(pass)));
          setVisitorPasses({ passes: passes.data.passes, metadata: passes.data.metadata });
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [visitorPassFilter, passPolicies, isModelOpen, passProps.isVisible, editPassProps.modalOpen]);

  const randomKey = () => {
    return Math.random().toString(36).substring(2, 9);
  };

  const applySorting = (key: string) => {
    if (sorting.key === key) {
      setSorting({ ...sorting, asc: !sorting.asc });
    } else {
      setSorting({ key: key, asc: true });
    }
  };
  useEffect(() => {
    //updated the logic for lint error -- check again if it is correct
    const sortedPasses = (visitorPasses?.passes ?? []).sort((a, b) => {
      if (sorting.key === 'date') {
        const aDate = a[sorting.key] ? dayjs(a[sorting.key]) : null;
        const bDate = b[sorting.key] ? dayjs(b[sorting.key]) : null;

        if (!aDate && !bDate) return 0;
        if (!aDate) return sorting.asc ? 1 : -1;
        if (!bDate) return sorting.asc ? -1 : 1;

        return sorting.asc ? aDate.diff(bDate) : bDate.diff(aDate);
      }
      if (a[sorting.key] < b[sorting.key]) {
        return sorting.asc ? -1 : 1;
      }
      if (a[sorting.key] > b[sorting.key]) {
        return sorting.asc ? 1 : -1;
      }
      return 0;
    });
    if (visitorPasses?.metadata) {
      setVisitorPasses({ passes: sortedPasses, metadata: visitorPasses.metadata });
    }
  }, [sorting]);

  const disableEdit = (pass: VisitorPass) => {
    const policy = passPolicies?.find(pol => pol.passTypeId === pass.passTypeId);
    const passesRemaining =
      (policy?.passesAllowed || 0) -
      (policy?.passesUsed == null
        ? 0
        : policy?.passesUsed > policy?.passesAllowed
          ? policy?.passesAllowed
          : policy?.passesUsed);
    if (pass.passTypeId === AppConfig.passType.vip) {
      if (policy?.operationalFlags.canEdit) {
        if (
          (pass.passStatusId === AppConfig.passStatus.used ||
            pass.passStatusId === AppConfig.passStatus.checkedIn ||
            pass.passStatusId === AppConfig.passStatus.expired) &&
          passesRemaining <= 0
        ) {
          return true;
        }
      } else return true;
    }
    if (
      pass.passStatusId === AppConfig.passStatus.used ||
      pass.passStatusId === AppConfig.passStatus.checkedIn ||
      pass.passStatusId === AppConfig.passStatus.expired
    ) {
      return true;
    }
    return false;
  };

  const disableDecline = (pass: VisitorPass) => {
    const policy = passPolicies?.find(pol => pol.passTypeId === pass.passTypeId);
    if (
      pass.statusId == AppConfig.passStatus.canceled ||
      pass.statusId === AppConfig.passStatus.expired ||
      pass.statusId === AppConfig.passStatus.used ||
      pass.statusId === AppConfig.passStatus.checkedIn
    ) {
      return true;
    }
    if (policy?.passTypeId === AppConfig.passType.vip) {
      if (policy?.operationalFlags.canCancel) {
        return false;
      }
      return true;
    }
    if (policy?.policyLocationId === 156) {
      return true;
    }
    return false;
  };
  const disableResendEmail = (pass: VisitorPass) => {
    const policy = passPolicies?.find(pol => pol.passTypeId === pass.passTypeId);
    if (
      !pass.emailAddress ||
      pass.passStatusId === AppConfig.passStatus.canceled ||
      pass.passStatusId === AppConfig.passStatus.expired ||
      pass.passStatusId === AppConfig.passStatus.checkedIn ||
      (pass.passLocationId !== 32000 && pass.passStatusId == AppConfig.passStatus.used)
    )
      return true;
    if (policy?.passTypeId === AppConfig.passType.vip) {
      if (policy?.operationalFlags.canCancel) {
        return false;
      }
      return true;
    }
    if (policy?.policyLocationId === 156) {
      return true;
    }
    return false;
  };

  return (
    <section className="eds-spacing--px-48">
      <VisitorPassHeader
        passTypes={passTypes}
        pageData={{
          currentPage: visitorPasses?.metadata.currentPage || 0,
          totalPages: visitorPasses?.metadata.totalPages || 0,
        }}
        passStatusOpts={PASSSTATUSES}
        handleFilterAndPageChange={filterAndPageChange}
      ></VisitorPassHeader>
      <LegalTextBanner />
      {loading ? (
        <>
          {Array.from({ length: 10 }).map((_, index) => (
            <Skeleton
              data-testid={`pass-skeleton ${index}`}
              key={index}
              height={50}
              width={`${100}%`}
              style={{ margin: 'var(--eds-space-16)' }}
            />
          ))}
        </>
      ) : (
        <div
          className={`eds-flex eds-flex--justify-content-center eds-flex--align-items-flex-center`}
        >
          <Table width="100%" className={styles.table_container}>
            <thead>
              <tr key={'passheader'} className="grid-header">
                {columns.map(column => {
                  if (
                    column.headerName === ts.GRID_HEADER_COMPANY.stringKey &&
                    visitorPassFilter.passType === 10
                  ) {
                    return null;
                  } else {
                    return (
                      <TableHeading
                        className={styles.table_header}
                        data-testid={column.headerName}
                        key={column.headerName}
                        onClick={() => applySorting(column.field)}
                      >
                        {i18nString(ts[column.headerName as TsKeys])}
                        {sorting.key === column.field &&
                          (sorting.asc ? (
                            <Icon name="CaretUp" data-testid="CaretUp" />
                          ) : (
                            <Icon name="CaretDown" />
                          ))}
                      </TableHeading>
                    );
                  }
                })}
                <TableHeading className={styles.table_header} key={'actions'}>
                  {i18nString(ts.GRID_HEADER_ACTIONS)}
                </TableHeading>
              </tr>
            </thead>
            <tbody>
              {visitorPasses?.passes &&
                visitorPasses?.passes.map(cell => {
                  return (
                    <tr key={'pass' + randomKey()} className={styles.table_row}>
                      <TableCell
                        className={styles.table_td}
                        key={cell.id}
                        data-label={i18nString(ts[columns[0].headerName as TsKeys])}
                      >
                        {cell.firstName} {cell.lastName}
                      </TableCell>
                      {visitorPassFilter.passType != 10 && (
                        <TableCell
                          className={styles.table_td}
                          key={cell.id + '-company'}
                          data-label={i18nString(ts[columns[1].headerName as TsKeys])}
                        >
                          {cell.companyName}
                        </TableCell>
                      )}
                      <TableCell
                        className={styles.table_td}
                        key={cell.id + '-type'}
                        data-label={i18nString(ts[columns[2].headerName as TsKeys])}
                      >
                        {i18nString(ts[cell.passTypeToken as TsKeys])}
                      </TableCell>
                      <TableCell
                        className={styles.table_td}
                        key={cell.id + '-policy'}
                        data-label={i18nString(ts[columns[3].headerName as TsKeys])}
                      >
                        {cell.passPolicy}
                      </TableCell>
                      <TableCell
                        className={styles.table_td}
                        key={cell.id + '-date'}
                        data-label={i18nString(ts[columns[4].headerName as TsKeys])}
                      >
                        {formatDate(cell)}
                      </TableCell>
                      <TableCell
                        className={styles.table_td}
                        key={cell.id + '-status'}
                        data-label={i18nString(ts[columns[5].headerName as TsKeys])}
                      >
                        {i18nString(ts[cell.statusToken as TsKeys])}
                      </TableCell>
                      <TableCell
                        key={cell.id + '-actions'}
                        data-label={i18nString(ts.GRID_HEADER_ACTIONS)}
                      >
                        <IconButton
                          label="edit pass"
                          aria-label="edit pass"
                          size="small"
                          disabled={disableEdit(cell)}
                          onClick={() => {
                            setEditPassProps({
                              ...editPassProps,
                              modalOpen: true,
                              passToEdit: cell,
                            });
                          }}
                          icon={'Edit'}
                          variant="ghost"
                        ></IconButton>
                        <IconButton
                          label="resend email"
                          aria-label="resend email"
                          size="small"
                          onClick={() => {
                            setPassProps({ ...passProps, isResendVisible: true, pass: cell });
                          }}
                          disabled={disableResendEmail(cell)}
                          icon={'InboxFilled'}
                          variant="ghost"
                          data-testid="resend-email"
                        ></IconButton>
                        <IconButton
                          label="decline pass"
                          aria-label="decline pass"
                          size="small"
                          onClick={() => {
                            setPassProps({ ...passProps, isVisible: true, pass: cell });
                          }}
                          disabled={disableDecline(cell)}
                          icon={'Close'}
                          variant="ghost"
                          data-testid="decline-pass"
                        ></IconButton>
                      </TableCell>
                    </tr>
                  );
                })}
              {visitorPasses?.passes?.length === 0 && (
                <tr>
                  <TableCell
                    className={styles.table_td}
                    colSpan={6}
                    data-label={i18nString(ts.GRID_HEADER_NAME)}
                  >
                    {i18nString(ts.GRID_EMPTY_TEXT_PASS)}
                  </TableCell>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}
      <CancelPassModal {...passProps} setSnackbar={setSnackBar} />
      <ResendEmailModal {...passProps} setSnackbar={setSnackBar} />
      {!!editPassProps.passToEdit && <Pass {...editPassProps} setPassSnackbar={setSnackBar} />}
    </section>
  );
};
